import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, unref as _unref } from "vue"

const _hoisted_1 = { class: "truck-map-container" }
const _hoisted_2 = { class: "mb-3" }
const _hoisted_3 = { class: "d-flex align-center justify-space-between mb-1" }
const _hoisted_4 = {
  key: 0,
  class: "no-gps-section mt-2 pa-2"
}
const _hoisted_5 = { class: "d-flex align-center mb-2 ga-2" }
const _hoisted_6 = { class: "no-gps-scroll-container d-flex ga-2 pb-1" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "operability-bar-wrapper mb-1" }
const _hoisted_9 = { class: "operability-stacked-bar" }
const _hoisted_10 = { class: "d-flex justify-space-between align-center ga-2" }
const _hoisted_11 = { class: "d-flex justify-space-between align-center mt-1 style-details" }
const _hoisted_12 = { class: "device-model text-truncate" }
const _hoisted_13 = { class: "ip-text" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import maplibregl, { Map, Popup, Marker, LngLatLike } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

// IMPORTACIÓN DE IMÁGENES
import camionIcon from '@/assets/Icons/camion.jpg';
import solarTrailerIcon from '@/assets/Icons/solartrailer.jpg';

import CONSTMAPS from '@/constants/maps';
import { getDeviceModel } from '@/utils/SmartLink/device';
import { LATENCY } from '@/interfaces/SmartLink/Topology';
import { OPERABILITY } from '@/interfaces/SmartLink/Equipos';

interface Props {
    trucks?: LATENCY[];
    operabilityData?: OPERABILITY[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TruckMapView',
  props: {
    trucks: { default: () => [] },
    operabilityData: { default: () => [] }
  },
  emits: ['select-device'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const wrapperRef = ref<HTMLElement | null>(null);
const mapTruckContainer = ref<HTMLElement | null>(null);
const isFullscreen = ref(false);
const searchQuery = ref('');

let mapInstance: Map | null = null;
let activePopup: Popup | null = null;
let currentMarkers: Marker[] = [];

const centerMap = ref<LngLatLike>([
    CONSTMAPS.longitud.value,
    CONSTMAPS.latitud.value,
]);

// EVALUAR COINCIDENCIA DE ESTADO SEGÚN LA LATENCIA
function checkStatusMatch(latencia: number, query: string): boolean {
    const q = query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
    if (!q) return false;

    if (latencia < 0) {
        const terms = ['off', 'sin servicio', 'sin serv', 'down', 'desconectado', 'fuera de servicio'];
        return terms.some(t => t.includes(q));
    }
    if (latencia <= 100) {
        const terms = ['ok', 'en linea', 'optimo', 'excelente', 'bueno', 'online'];
        return terms.some(t => t.includes(q));
    }
    if (latencia <= 500) {
        const terms = ['alerta', 'warning', 'leve'];
        return terms.some(t => t.includes(q));
    }
    const terms = ['alarma', 'critico', 'error'];
    return terms.some(t => t.includes(q));
}

// FILTRADO INTERNO POR BÚSQUEDA (NOMBRE, IP, MODELO, TIPO, SUBTIPO Y ESTADO)
const searchedTrucks = computed(() => {
    if (!searchQuery.value) return props.trucks || [];
    const query = searchQuery.value.toLowerCase().trim();

    return (props.trucks || []).filter(item => {
        const nameMatch = item.name?.toLowerCase().includes(query);
        const ipMatch = item.ip?.toLowerCase().includes(query);
        const modelMatch = getDeviceModel(item.anotacion)?.toLowerCase().includes(query);
        
        const subtipoMatch = item.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query);
        const tipoMatch = item.tipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query);
        
        const statusMatch = checkStatusMatch(item.latencia, query);

        return nameMatch || ipMatch || modelMatch || subtipoMatch || tipoMatch || statusMatch;
    });
});

// FILTRADO PARA MOSTRAR CLIENTES Y ACCESOS
const filteredVisibleDevices = computed(() => {
    const safeTrucks = searchedTrucks.value;

    return safeTrucks.filter(item => {
        if (!item?.subtipo) return false;

        const subtipoNorm = item.subtipo.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

        const isCliente = subtipoNorm === 'cliente';
        const isAcceso = subtipoNorm === 'acceso';

        return isCliente || isAcceso;
    });
});

const uniqueItems = computed(() => {
    return filteredVisibleDevices.value.filter(
        (item, index, self) => index === self.findIndex((t) => t.ip === item.ip)
    );
});

const equiposConGps = computed(() => {
    return uniqueItems.value.filter(
        (e) =>
            e.latitud !== undefined &&
            e.longitud !== undefined &&
            Number(e.latitud) !== -1 &&
            Number(e.longitud) !== -1 &&
            Number(e.latitud) !== 0 &&
            Number(e.longitud) !== 0
    );
});

const equiposSinGps = computed(() => {
    return uniqueItems.value.filter(
        (e) =>
            e.latitud === undefined ||
            e.longitud === undefined ||
            Number(e.latitud) === -1 ||
            Number(e.longitud) === -1 ||
            Number(e.latitud) === 0 ||
            Number(e.longitud) === 0
    );
});

function getLatencyColor(latencia: number): string {
    if (latencia < 0) return '#9E9E9E';   
    if (latencia <= 100) return '#00E676'; 
    if (latencia <= 500) return '#FFEA00'; 
    return '#FF3D00';                      
}

function getOperabilityPercentages(ip: string) {
    const safeData = props.operabilityData || [];
    const item = safeData.find(op => op.ip === ip);
    if (!item) return { ok: 0, alert: 0, alarm: 0, down: 100 };
    const total = (item.ok || 0) + (item.alert || 0) + (item.alarm || 0) + (item.down || 0);
    if (total === 0) return { ok: 0, alert: 0, alarm: 0, down: 100 };
    return { 
        ok: ((item.ok || 0) / total) * 100, 
        alert: ((item.alert || 0) / total) * 100, 
        alarm: ((item.alarm || 0) / total) * 100, 
        down: ((item.down || 0) / total) * 100 
    };
}

function formatShortDate(dateStr?: string) {
    if (!dateStr) return 'N/A';
    const parts = dateStr.trim().split(' ');
    return parts.length < 2 ? dateStr : `${parts[0]} ${parts[1].substring(0, 5)}`;
}

const clearMarkers = () => {
    currentMarkers.forEach((marker) => marker.remove());
    currentMarkers = [];
};

const renderMapMarkers = () => {
    if (!mapInstance) return;
    clearMarkers();

    equiposConGps.value.forEach((equipo) => {
        const latColor = getLatencyColor(equipo.latencia);
        const modelText = getDeviceModel(equipo.anotacion);
        const dateText = formatShortDate(equipo.fecha);
        const op = getOperabilityPercentages(equipo.ip);
        const latDisplay = equipo.latencia < 0 ? "OFF" : `${equipo.latencia}ms`;

        const subtipoNorm = equipo.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        const isAcceso = subtipoNorm === 'acceso';
        const iconSrc = isAcceso ? solarTrailerIcon : camionIcon;
        const iconAlt = isAcceso ? 'Acceso (Solar Trailer)' : 'Cliente (Camión)';

        const el = document.createElement("div");
        el.className = "custom-icon-marker";

        el.innerHTML = `
            <img src="${iconSrc}" class="device-img" alt="${iconAlt}" />
            <div class="marker-name-only" style="color: ${latColor};">
                ${equipo.name}
            </div>
        `;

        el.addEventListener("click", (e) => {
            e.stopPropagation();
            emit("select-device", equipo);
        });

        el.addEventListener("mouseenter", () => {
            if (!mapInstance) return;

            const htmlContent = `
                <div style="font-family: sans-serif; padding: 4px; font-size: 0.78rem; color: #0F172A;">
                    <b style="font-size: 0.85rem; color: #0F172A;">${equipo.name}</b><br/>
                    <span style="color: #0284C7;"><b>Modelo:</b> ${modelText}</span><br/>
                    <span><b>IP:</b> ${equipo.ip}</span><br/>
                    <span><b>Tipo:</b> <span style="text-transform: capitalize;">${equipo.subtipo}</span></span><br/>
                    <span><b>Latencia:</b> ${latDisplay}</span><br/>
                    <span><b>Última act.:</b> ${dateText}</span>
                    <hr style="border-top: 1px solid #CBD5E1; margin: 4px 0;" />
                    <b>Operatividad:</b><br/>
                    <span style="color: #16A34A;">● Excelente: ${op.ok.toFixed(1)}%</span><br/>
                    <span style="color: #CA8A04;">● Alerta: ${op.alert.toFixed(1)}%</span><br/>
                    <span style="color: #DC2626;">● Alarma: ${op.alarm.toFixed(1)}%</span><br/>
                    <span style="color: #64748B;">● Fuera de serv.: ${op.down.toFixed(1)}%</span>
                </div>
            `;

            if (activePopup) activePopup.remove();
            activePopup = new maplibregl.Popup({ closeButton: false, offset: 15 })
                .setLngLat([Number(equipo.longitud), Number(equipo.latitud)])
                .setHTML(htmlContent)
                .addTo(mapInstance);
        });

        el.addEventListener("mouseleave", () => {
            if (activePopup) {
                activePopup.remove();
                activePopup = null;
            }
        });

        const marker = new maplibregl.Marker({ element: el })
            .setLngLat([Number(equipo.longitud), Number(equipo.latitud)])
            .addTo(mapInstance);

        currentMarkers.push(marker);
    });
};

watch(
    () => [props.trucks, props.operabilityData, searchQuery.value],
    () => {
        if (mapInstance) {
            renderMapMarkers();
        }
    },
    { deep: true }
);

function inicializarMapa() {
    if (!mapTruckContainer.value || mapInstance) return;

    mapInstance = new maplibregl.Map({
        container: mapTruckContainer.value,
        center: centerMap.value,
        zoom: 14,
        pitch: 35,
        bearing: 20,
        style: "https://api.maptiler.com/maps/satellite/style.json?key=6JWVt6LFaY22nquimZpF",
    });

    mapInstance.addControl(new maplibregl.NavigationControl(), "top-right");

    mapInstance.on("load", () => {
        setTimeout(() => {
            if (mapInstance) {
                mapInstance.resize();
                renderMapMarkers();
            }
        }, 300);
    });
}

function toggleFullscreen() {
    if (!wrapperRef.value) return;
    if (!document.fullscreenElement) {
        wrapperRef.value.requestFullscreen().then(() => {
            isFullscreen.value = true;
            nextTick(() => mapInstance?.resize());
        });
    } else {
        document.exitFullscreen().then(() => {
            isFullscreen.value = false;
            nextTick(() => mapInstance?.resize());
        });
    }
}

onMounted(() => {
    nextTick(() => {
        inicializarMapa();
    });
});

onUnmounted(() => {
    clearMarkers();
    if (mapInstance) mapInstance.remove();
});

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-caption font-weight-black text-uppercase tracking-wider text-cyan-accent-1" }, " Filtrar Mapa ", -1)),
        (searchQuery.value)
          ? (_openBlock(), _createBlock(_component_v_chip, {
              key: 0,
              size: "x-small",
              color: "cyan",
              variant: "tonal",
              class: "font-weight-bold",
              style: {"height":"16px","font-size":"0.58rem"}
            }, {
              default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                _createTextVNode(" Filtro Activo ", -1)
              ]))]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _createVNode(_component_v_text_field, {
        modelValue: searchQuery.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
        density: "compact",
        variant: "outlined",
        "hide-details": "",
        placeholder: "Buscar por Nombre, IP, Modelo, Tipo (Acceso, Cliente) o Estado (En línea, Alerta, Alarma, OFF)...",
        "prepend-inner-icon": "mdi-magnify",
        clearable: "",
        class: "neon-search-field"
      }, null, 8, ["modelValue"])
    ]),
    _createElementVNode("div", {
      ref_key: "wrapperRef",
      ref: wrapperRef,
      class: "topology-wrapper"
    }, [
      _createElementVNode("div", {
        ref_key: "mapTruckContainer",
        ref: mapTruckContainer,
        class: "mapTruckContainer"
      }, null, 512),
      _createVNode(_component_v_btn, {
        icon: "",
        size: "small",
        color: "slate-800",
        class: "fullscreen-btn",
        onClick: toggleFullscreen
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_icon, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(isFullscreen.value ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ], 512),
    (equiposSinGps.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_v_icon, {
              size: "x-small",
              color: "warning"
            }, {
              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                _createTextVNode("mdi-map-marker-off", -1)
              ]))]),
              _: 1
            }),
            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "no-gps-title font-weight-bold" }, " Equipos sin ubicación GPS ", -1)),
            _createVNode(_component_v_chip, {
              size: "x-small",
              color: "grey-darken-2",
              variant: "flat",
              class: "px-1",
              style: {"height":"16px","font-size":"0.65rem"}
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(equiposSinGps.value.length), 1)
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(equiposSinGps.value, (equipo) => {
              return (_openBlock(), _createElementBlock("div", {
                key: equipo.ip,
                class: "no-gps-card pa-1 px-2",
                onClick: ($event: any) => (_ctx.$emit('select-device', equipo))
              }, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", {
                      class: "bar-segment",
                      style: _normalizeStyle([{"background-color":"#00E676"}, { width: getOperabilityPercentages(equipo.ip).ok + '%' }])
                    }, null, 4),
                    _createElementVNode("div", {
                      class: "bar-segment",
                      style: _normalizeStyle([{"background-color":"#FFEA00"}, { width: getOperabilityPercentages(equipo.ip).alert + '%' }])
                    }, null, 4),
                    _createElementVNode("div", {
                      class: "bar-segment",
                      style: _normalizeStyle([{"background-color":"#FF3D00"}, { width: getOperabilityPercentages(equipo.ip).alarm + '%' }])
                    }, null, 4),
                    _createElementVNode("div", {
                      class: "bar-segment",
                      style: _normalizeStyle([{"background-color":"#9E9E9E"}, { width: getOperabilityPercentages(equipo.ip).down + '%' }])
                    }, null, 4)
                  ])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("span", {
                    class: "device-name font-weight-bold text-truncate",
                    style: _normalizeStyle({ color: getLatencyColor(equipo.latencia) })
                  }, _toDisplayString(equipo.name), 5),
                  _createElementVNode("span", {
                    class: "any-badge font-weight-bold",
                    style: _normalizeStyle({ color: getLatencyColor(equipo.latencia) })
                  }, _toDisplayString(equipo.latencia < 0 ? 'OFF' : `${equipo.latencia}ms`), 5)
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(getDeviceModel)(equipo.anotacion)), 1),
                  _createElementVNode("span", _hoisted_13, _toDisplayString(equipo.ip), 1)
                ])
              ], 8, _hoisted_7))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})